"use client"
import { useRef, useEffect, useMemo, useState } from "react"
import dynamic from "next/dynamic"
import { motion, useInView, AnimatePresence, useAnimationFrame } from "motion/react"
import { VelocityMarquee } from "@/components/scroll/VelocityMarquee"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import { useHydrated } from "@/hooks/useHydrated"
import Image from "next/image"
import { Footer } from "@/components/layout/Footer"
import { GHLBookingWidget } from "@/components/ui/GHLBookingWidget"
import { HeroRings, DotGrid, FloatingDots, ScanLine, CornerAccents } from "@/components/ui/BackgroundDecor"
import { WebGLErrorBoundary } from "@/components/three/WebGLErrorBoundary"
import { KeyTakeaways, FaqSection } from "@/components/seo/AeoBlocks"
import { useSiteSettings } from "@/components/site/SiteSettingsProvider"
import { excerptQuote, reviewAttribution, REVIEW_LINK_REL, REVIEW_LINK_STYLE } from "@/lib/review-display"
import type { HomeContent } from "./home-content"

// Client-only: the server has no navigator.gpu, so AmbientGrassField's WebGPU
// availability check would always take the "unavailable" branch during SSR,
// then potentially diverge once a real (WebGPU-capable) browser hydrates —
// a guaranteed hydration mismatch. ssr:false defers the decision entirely to
// the client, where navigator.gpu reflects the actual visiting browser.
const AmbientGrassField = dynamic(
  () => import("@/components/three/AmbientGrassField").then((m) => m.AmbientGrassField),
  { ssr: false }
)

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const SURFACE = "rgba(11,15,28,0.82)"
const BORDER = "rgba(255,255,255,0.08)"


function Section({ id, children, style, className }: { id?: string; children: React.ReactNode; style?: React.CSSProperties; className?: string }) {
  return (
    <section
      id={id}
      className={className}
      style={{ padding: "80px clamp(20px, 4vw, 48px)", maxWidth: 1400, margin: "0 auto", ...style }}
    >
      {children}
    </section>
  )
}

function Eyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
      <motion.span
        initial={{ width: 0 }}
        whileInView={{ width: 32 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
        style={{ height: 1, background: ACCENT, flexShrink: 0, display: "block" }}
      />
      <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>{label}</span>
    </div>
  )
}

function Counter({ to, suffix = "" }: { to: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.1 })
  const decimals = Number.isInteger(to) ? 0 : 1
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    if (!inView) return
    let start = 0
    let raf: number
    const tick = (ts: number) => {
      if (!start) start = ts
      const p = Math.min((ts - start) / 1600, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      setCount(p < 1 ? parseFloat((eased * to).toFixed(decimals)) : to)
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, to, decimals])

  return <span ref={ref}>{count ?? to}{suffix}</span>
}

function ServiceCard({ number, title, description, tags, href }: { number: string; title: string; description: string; tags: string[]; href: string }) {
  const [hovered, setHovered] = useState(false)
  return (
    <motion.a
      href={href}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{ borderColor: hovered ? ACCENT : BORDER }}
      style={{ padding: "48px 40px", borderRadius: 20, border: `1px solid ${BORDER}`, background: SURFACE, display: "flex", flexDirection: "column", gap: 24, position: "relative", overflow: "hidden", cursor: "default" }}
    >
      <motion.div animate={{ opacity: hovered ? 1 : 0 }} style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)` }} />
      <span style={{ fontSize: 11, letterSpacing: "0.15em", color: MUTED, textTransform: "uppercase" }}>{number}</span>
      <div>
        <h3 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 16 }}>{title}</h3>
        <p style={{ color: MUTED, lineHeight: 1.7, fontSize: 16 }}>{description}</p>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: "auto" }}>
        {tags.map(t => <span key={t} style={{ padding: "4px 14px", borderRadius: 100, border: `1px solid ${BORDER}`, fontSize: 12, color: MUTED }}>{t}</span>)}
      </div>
      <motion.div animate={{ x: hovered ? 0 : 16, opacity: hovered ? 1 : 0 }} style={{ fontSize: 13, fontWeight: 700, color: ACCENT }}>Learn more →</motion.div>
    </motion.a>
  )
}

const CASE_GRADIENTS = {
  navy: "linear-gradient(135deg, #1a1f35 0%, #0d1117 100%)",
  orange: `linear-gradient(135deg, rgba(196,84,26,0.22) 0%, #0d1117 100%)`,
} as const

function CaseCard({ tag, title, outcome, accent, href }: { tag: string; title: string; outcome: string; accent: "navy" | "orange"; href: string }) {
  const [hovered, setHovered] = useState(false)
  return (
    <motion.a
      href={href}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{ scale: hovered ? 1.02 : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      style={{ borderRadius: 20, overflow: "hidden", background: CASE_GRADIENTS[accent], minHeight: 400, padding: "48px 40px", display: "flex", flexDirection: "column", justifyContent: "space-between", cursor: "pointer" }}
    >
      <span style={{ display: "inline-block", padding: "4px 14px", borderRadius: 100, background: "rgba(255,255,255,0.1)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", width: "fit-content" }}>{tag}</span>
      <div>
        <h3 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 16 }}>{title}</h3>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: ACCENT, flexShrink: 0, display: "inline-block" }} />{outcome}
        </p>
      </div>
      <motion.div animate={{ opacity: hovered ? 1 : 0, x: hovered ? 0 : -8 }} style={{ fontSize: 13, fontWeight: 700 }}>View case study →</motion.div>
    </motion.a>
  )
}

function ProcessStep({ number, title, description }: { number: string; title: string; description: string }) {
  return (
    <motion.div
      whileHover={{ x: 6 }}
      transition={{ type: "spring", stiffness: 300, damping: 28 }}
      style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 24, paddingBottom: 48, borderBottom: `1px solid ${BORDER}` }}
    >
      <span style={{ fontSize: "clamp(32px, 5vw, 44px)", fontWeight: 900, color: ACCENT, letterSpacing: "-0.04em", lineHeight: 1 }}>{number}</span>
      <div>
        <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, letterSpacing: "-0.02em" }}>{title}</h3>
        <p style={{ color: MUTED, lineHeight: 1.7, fontSize: 15 }}>{description}</p>
      </div>
    </motion.div>
  )
}

function ProcessGlow() {
  const ref1 = useRef<HTMLDivElement>(null)
  const ref2 = useRef<HTMLDivElement>(null)
  useAnimationFrame((t) => {
    if (ref1.current) {
      const x = Math.sin(t * 0.00056) * 120
      const y = Math.cos(t * 0.00038) * 80
      ref1.current.style.transform = `translate(${x}px, ${y}px)`
    }
    if (ref2.current) {
      const x = Math.cos(t * 0.00044) * 90
      const y = Math.sin(t * 0.00068) * 70
      ref2.current.style.transform = `translate(${x}px, ${y}px)`
    }
  })
  return (
    <>
      <div ref={ref1} style={{ position: "absolute", inset: -160, pointerEvents: "none", background: `radial-gradient(ellipse 58% 52% at 60% 40%, rgba(196,84,26,0.30) 0%, transparent 68%)`, willChange: "transform" }} />
      <div ref={ref2} style={{ position: "absolute", inset: -120, pointerEvents: "none", background: `radial-gradient(ellipse 44% 40% at 38% 62%, rgba(196,84,26,0.18) 0%, transparent 62%)`, willChange: "transform" }} />
    </>
  )
}

// ── Rotating Testimonials ──────────────────────────────────────────────────
function RotatingTestimonials({ reviews }: { reviews: HomeContent["testimonials"] }) {
  const hydrated = useHydrated()
  const [current, setCurrent] = useState(0)
  const [dir, setDir] = useState(1)
  // Static curated reviews + live consented submissions (see /api/testimonials).
  // Every quote is held to the curated length so no slide outgrows the others.
  type Slide = { quote: string; name: string; role: string; image: string | null; href?: string | null }
  const curated = useMemo<Slide[]>(() => reviews.map((r) => ({ ...r, quote: excerptQuote(r.quote) })), [reviews])
  const [allReviews, setAllReviews] = useState<Slide[]>(curated)
  useEffect(() => { setAllReviews(curated) }, [curated])

  useEffect(() => {
    fetch("/api/testimonials")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const live: Slide[] = ((d?.testimonials ?? []) as Array<{ text: string; name: string; rating: number; company?: string | null; role?: string | null; about?: string | null; image?: string | null; website?: string | null }>)
          .slice(0, 6)
          .map((t) => {
            const { title, subtitle } = reviewAttribution(t)
            return {
              quote: excerptQuote(t.text),
              name: title,
              role: subtitle ?? `${"★".repeat(t.rating)} — verified client review`,
              image: t.image ?? null,
              href: t.website ?? null,
            }
          })
        if (!live.length) return
        // A client who reviews again through /review replaces their curated
        // slide (keeping its photo if they didn't upload one) instead of
        // appearing twice.
        const key = (n: string) => n.trim().toLowerCase()
        const merged = curated.map((c) => {
          const fresh = live.find((l) => key(l.name) === key(c.name))
          return fresh ? { ...fresh, image: fresh.image ?? c.image } : c
        })
        const extra = live.filter((l) => !merged.some((m) => key(m.name) === key(l.name)))
        setAllReviews([...merged, ...extra])
      })
      .catch(() => {})
  }, [curated])

  useEffect(() => {
    const timer = setInterval(() => {
      setDir(1)
      setCurrent(c => (c + 1) % allReviews.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [allReviews.length])

  const go = (idx: number) => {
    setDir(idx > current ? 1 : -1)
    setCurrent(idx)
  }

  const review = allReviews[current] ?? allReviews[0]

  return (
    <div style={{ padding: "80px clamp(20px, 4vw, 48px)", background: SURFACE, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
      <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={current}
            custom={dir}
            initial={hydrated ? { opacity: 0, y: dir * 24 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dir * -24 }}
            transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
          >
            <p style={{ fontSize: "clamp(18px, 2.8vw, 34px)", fontWeight: 700, lineHeight: 1.35, letterSpacing: "-0.02em", marginBottom: 48 }}>
              &ldquo;{review.quote}&rdquo;
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", flexShrink: 0, position: "relative", outline: "2px solid #C4541A", outlineOffset: "2px", background: "rgba(196,84,26,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {review.image ? (
                  <Image src={review.image} alt={review.name} fill style={{ objectFit: "cover" }} sizes="48px" />
                ) : (
                  <span style={{ fontWeight: 800, fontSize: 18, color: "#C4541A" }}>{review.name.charAt(0)}</span>
                )}
              </div>
              <div style={{ textAlign: "left" }}>
                <p style={{ fontWeight: 700, fontSize: 15 }}>
                  {review.href ? <a href={review.href} target="_blank" rel={REVIEW_LINK_REL} style={REVIEW_LINK_STYLE}>{review.name}</a> : review.name}
                </p>
                <p style={{ color: MUTED, fontSize: 13 }}>{review.role}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Dot nav */}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 40 }}>
          {allReviews.map((_, i) => (
            <button
              key={i}
              onClick={() => go(i)}
              aria-label={`Review ${i + 1}`}
              style={{
                width: i === current ? 28 : 8,
                height: 8,
                borderRadius: 100,
                background: i === current ? ACCENT : "rgba(255,255,255,0.2)",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "width 0.35s ease, background 0.35s ease",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function HomeClient({ content }: { content: HomeContent }) {
  const heroRef = useRef<HTMLElement>(null)
  const hydrated = useHydrated()
  const settings = useSiteSettings()

  // The grass field is a fixed decorative canvas behind all content (z-index 0).
  // Mounting it during hydration pulled the three.js/WebGPU chunk into the critical
  // path and cost ~2s of main-thread work before the hero could paint. Deferring it
  // to the first idle callback keeps the visual identical a beat later while taking
  // it off the critical path entirely. Falls back to a timeout on browsers without
  // requestIdleCallback (Safari).
  // Deferring to idle was not enough on mobile: the three.js/WebGPU chunk is the
  // single largest first-party script on the page, and on a throttled phone CPU its
  // parse + first frames dominated main-thread work (TBT 11.8s, TTI 18s) even when
  // it started late. It is decoration, so on a small or low-powered device we simply
  // never mount it — desktop, where it is the showcase piece, is unchanged.
  const [decorReady, setDecorReady] = useState(false)
  useEffect(() => {
    const nav = navigator as Navigator & { deviceMemory?: number }
    const wantsMotion = !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const bigScreen = window.matchMedia("(min-width: 1024px)").matches
    const capable =
      (nav.hardwareConcurrency ?? 8) >= 4 && (nav.deviceMemory ?? 8) >= 4
    if (!wantsMotion || !bigScreen || !capable) return

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => setDecorReady(true), { timeout: 2500 })
      return () => w.cancelIdleCallback?.(id)
    }
    const t = window.setTimeout(() => setDecorReady(true), 1200)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      {decorReady && (
        <WebGLErrorBoundary fallback={<></>}>
          <AmbientGrassField />
        </WebGLErrorBoundary>
      )}

      {/* Establishes one stacking context above the fixed z-index:0 grass
          canvas for ALL page content, so no section can silently render
          behind it for lacking its own position/z-index. */}
      <div style={{ position: "relative", zIndex: 1 }}>

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="hero-section"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          padding: "0 clamp(20px, 4vw, 48px) 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decor */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 80% 65% at 76% 36%, rgba(196,84,26,0.40) 0%, transparent 66%)` }} />
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.15, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 44% 38% at 16% 78%, rgba(196,84,26,0.22) 0%, transparent 62%)` }}
        />
        <HeroRings side="right" />
        <DotGrid />
        <FloatingDots />
        <ScanLine />
        <CornerAccents />
        <motion.div
          animate={{ opacity: [0.025, 0.055, 0.025] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          style={{ position: "absolute", bottom: -60, right: -40, fontSize: "clamp(120px, 30vw, 420px)", fontWeight: 900, color: "#fff", userSelect: "none", pointerEvents: "none", letterSpacing: "-0.05em", lineHeight: 1 }}
        >01</motion.div>

        <div style={{ maxWidth: 1400, width: "100%", margin: "0 auto", position: "relative", zIndex: 1 }}>
          <motion.div
            initial={hydrated ? { opacity: 0, y: 24 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "6px 18px", border: `1px solid ${BORDER}`, borderRadius: 100, marginBottom: 48, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}
          >
            <motion.span
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, display: "block", flexShrink: 0 }}
            />
            {content.heroBadge}
          </motion.div>

          <motion.h1
            initial={hydrated ? { opacity: 0, y: 32 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.3 }}
            style={{ fontSize: "clamp(64px, 13vw, 200px)", fontWeight: 900, lineHeight: 0.88, letterSpacing: "-0.04em", margin: 0 }}
          >
            <span style={{ color: "#fff", display: "block" }}>{content.heroHeadingLine1}</span>
            <motion.span
              initial={hydrated ? { opacity: 0, y: 32 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.45 }}
              style={{ color: ACCENT, display: "block" }}
            >
              {content.heroHeadingLine2}
            </motion.span>
          </motion.h1>

          <motion.div
            initial={hydrated ? { opacity: 0, y: 24 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between"
            style={{ gap: 40, marginTop: 64, flexWrap: "wrap" }}
          >
            <p style={{ fontSize: "clamp(15px, 1.4vw, 20px)", color: MUTED, maxWidth: 480, lineHeight: 1.65, margin: 0 }}>
              {content.heroIntro}
            </p>
            <div className="hero-cta-wrap" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <motion.a href={content.heroPrimaryHref} whileHover={{ scale: 1.04, background: "#D4601F" }} whileTap={{ scale: 0.97 }} style={{ padding: "15px 36px", background: ACCENT, color: "#fff", borderRadius: 100, fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em", display: "inline-block" }}>{content.heroPrimaryLabel}</motion.a>
              <motion.a href={content.heroSecondaryHref} whileHover={{ borderColor: "rgba(255,255,255,0.3)" }} style={{ padding: "15px 36px", border: `1px solid ${BORDER}`, color: "#fff", borderRadius: 100, fontWeight: 600, fontSize: 15, letterSpacing: "-0.01em", display: "inline-block" }}>{content.heroSecondaryLabel}</motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── MARQUEE ──────────────────────────────────────────────── */}
      <div style={{ padding: "28px 0", borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <VelocityMarquee
          items={content.marqueeItems}
          baseVelocity={-2}
        />
      </div>

      {/* ── STATS ────────────────────────────────────────────────── */}
      <Section>
        <ScrollReveal effect="fade-up"><Eyebrow label={content.statsEyebrow} /></ScrollReveal>
        <div
          className="grid grid-cols-1 md:grid-cols-3"
          style={{ borderRadius: 20, overflow: "hidden", border: `1px solid ${BORDER}` }}
        >
          {content.stats.map(({ value, suffix, label }, i) => (
            <ScrollReveal key={label} effect="fade-up" delay={i * 0.1}>
              <div
                className={i < 2 ? "border-b md:border-b-0 md:border-r" : ""}
                style={{
                  padding: "clamp(40px, 5vw, 64px) clamp(24px, 4vw, 48px)",
                  background: i === 1 ? `rgba(67,36,27,0.85)` : SURFACE,
                  borderColor: BORDER,
                  height: "100%",
                  position: "relative",
                }}
              >
                {i === 1 && (
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5], scaleX: [0.7, 1, 0.7] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`, transformOrigin: "center" }}
                  />
                )}
                <div style={{ fontSize: "clamp(48px, 7vw, 96px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1, color: i === 1 ? ACCENT : "#fff", marginBottom: 20 }}>
                  <Counter to={value} suffix={suffix} />
                </div>
                <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.6, maxWidth: 240 }}>{label}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </Section>

      {/* ── SERVICES ─────────────────────────────────────────────── */}
      <Section id="services" style={{ paddingTop: 0 }}>
        <ScrollReveal effect="fade-up">
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between"
            style={{ marginBottom: 64, gap: 24 }}
          >
            <div>
              <Eyebrow label={content.servicesEyebrow} />
              <h2 style={{ fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92 }}>{content.servicesHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.servicesHeadingLine2}</span></h2>
            </div>
            <p style={{ color: MUTED, maxWidth: 360, lineHeight: 1.7, fontSize: 16 }}>{content.servicesIntro}</p>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 20 }}>
          {content.serviceCards.map((s, i) => (
            <ScrollReveal key={s.title} effect="fade-up" delay={i * 0.1}><ServiceCard {...s} /></ScrollReveal>
          ))}
        </div>
      </Section>

      {/* ── WORK ─────────────────────────────────────────────────── */}
      <Section id="work" style={{ paddingTop: 0 }}>
        <ScrollReveal effect="fade-up">
          <div
            className="flex flex-col md:flex-row md:items-end md:justify-between"
            style={{ marginBottom: 64, gap: 24 }}
          >
            <div>
              <Eyebrow label={content.workEyebrow} />
              <h2 style={{ fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92 }}>{content.workHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.workHeadingLine2}</span></h2>
            </div>
            <a href={content.workLinkHref} style={{ padding: "14px 32px", border: `1px solid ${BORDER}`, borderRadius: 100, fontSize: 14, fontWeight: 600, color: "#fff", flexShrink: 0 }}>{content.workLinkLabel}</a>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 20 }}>
          {content.workCards.map((c, i) => (
            <ScrollReveal key={c.title} effect="fade-up" delay={i * 0.1}><CaseCard {...c} /></ScrollReveal>
          ))}
        </div>
      </Section>

      {/* ── PROCESS ─────────────────────────────── */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <ProcessGlow />
        <FloatingDots />
        <Section id="process" style={{ paddingTop: 40 }}>
          <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: "clamp(48px, 6vw, 100px)", alignItems: "start" }}>
            <ScrollReveal effect="fade-up">
              <Eyebrow label={content.processEyebrow} />
              <h2 style={{ fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92 }}>{content.processHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.processHeadingLine2}</span></h2>
            </ScrollReveal>
            <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
              {content.processSteps.map((step, i) => (
                <ScrollReveal key={step.number} effect="fade-up" delay={i * 0.05}>
                  <ProcessStep {...step} />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* ── ROTATING TESTIMONIALS ────────────────────────────────── */}
      <RotatingTestimonials reviews={content.testimonials} />

      {/* ── CONTACT ──────────────────────────────────────────────── */}
      <div style={{ background: SURFACE, borderTop: `1px solid ${BORDER}` }}>
      <Section id="contact">
        <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: "clamp(40px, 6vw, 80px)", alignItems: "start" }}>
          <ScrollReveal effect="fade-up">
            <Eyebrow label={content.contactEyebrow} />
            <h2 style={{ fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.92, marginBottom: 32 }}>{content.contactHeadingLine1}<br /><span style={{ color: ACCENT }}>{content.contactHeadingLine2}</span></h2>
            <p style={{ color: MUTED, fontSize: 17, lineHeight: 1.7, maxWidth: 380, marginBottom: 48 }}>
              {content.contactIntro}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 48}}>
              {content.contactAssurances.map(text => (
                <div key={text} style={{ display: "flex", gap: 14, alignItems: "center", color: MUTED, fontSize: 15}}>
                  <span style={{ color: ACCENT, fontSize: 10, flexShrink: 0 }}>✦</span>{text}
                </div>
              ))}
            </div>

            <div>
              <p style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>Connect</p>
              <div style={{ display: "flex", gap: 20 }}>
                {(settings.socialLinks?.length
                  ? settings.socialLinks
                  : [
                      { label: "Instagram", href: "https://www.instagram.com/boldpiq/" },
                      { label: "Facebook", href: "https://www.facebook.com/boldpiq" },
                      { label: "Pinterest", href: "https://za.pinterest.com/boldpiq/" },
                      { label: "LinkedIn", href: "https://www.linkedin.com/company/boldpiq/" },
                      { label: "Google Maps", href: "https://www.google.com/maps/place/BoldPiq/@-33.8896839,18.5047937,17z/data=!3m1!4b1!4m6!3m5!1s0x1dcc5d74a5e42ecd:0x5d856bbf2cc68960!8m2!3d-33.8896839!4d18.5073686!16s%2Fg%2F11ykd7gz7t" },
                    ]
                ).map(s => (
                  <motion.a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ color: "#fff" }}
                    style={{ color: MUTED, fontSize: 14, textDecoration: "none" }}
                  >
                    {s.label}
                  </motion.a>
                ))}
              </div>
            </div>
            
          </ScrollReveal>

          <GHLBookingWidget
            src={settings.bookingSrc || "https://link.zip360.co.za/widget/booking/2iYXsaTBfL5b7Y870XVX"}
            id={`${settings.bookingId || "2iYXsaTBfL5b7Y870XVX"}_home`}
            title={settings.bookingTitle || "Book a Discovery Call"}
            defaultHeight={700}
            borderRadius={24}
            border={`1px solid ${BORDER}`}
            background={SURFACE}
            redirectTo="/thank-you"
            deferLoad
            lazy
          />
        </div>
      </Section>

      {/* ── ANSWERS FOR PEOPLE AND ASSISTANTS ────────────────────────
          A summary block and real question/answer pairs (with FAQPage
          JSON-LD) are the two formats AI assistants extract most reliably.
          Without them the page said plenty and answered nothing a search
          assistant could quote. */}
      <Section>
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 clamp(20px, 4vw, 48px)" }}>
          <KeyTakeaways items={content.takeaways} />
          <FaqSection faqs={content.faqs} heading={content.faqHeading} />
        </div>
      </Section>
      </div>

      {/* ── FOOTER ───────────────────────────────────────────────── */}
      <Footer />

      </div>
    </main>
  )
}
